import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  Stack,
  IconButton,
  Divider,
  Chip
} from '@mui/material';
import { Close, Save, Delete, CalendarToday, Person } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { Vocabulary } from '../../Utils/Vocabulary';
import { toast } from 'react-toastify';
import { getData } from '../../Services/getData';
import { postData } from '../../Services/postData';
import { deleteData } from '../../Services/deleteData';
import { endpoints } from '../../Utils/UrlEnum';

interface NotesModalProps {
  open: boolean;
  onClose: () => void;
  messageId?: string | null;
  messageName?: string;
}

interface Note {
  _id: string;
  text: string;
  createdAt: Date;
  agent?: { firstName: string; lastName: string; } | string;
}

export default function NotesModal({ open, onClose, messageId, messageName }: NotesModalProps) {
  const theme = useTheme();
  const [noteText, setNoteText] = useState('');
  const [notes, setNotes] = useState<Note[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open && messageId) {
      loadNotes();
    }
  }, [open, messageId]);

  const loadNotes = async () => {
    try {
      if (!messageId) return;
      const response = await getData(`${endpoints.messages.main}/${messageId}/notes`);
      setNotes(response.data || []);
    } catch (error) {
      console.error('Error loading notes:', error);
      setNotes([]);
    }
  };

  const handleSaveNote = async () => {
    if (!noteText.trim()) {
      toast.warning(Vocabulary.enterNoteText);
      return;
    }

    if (!messageId) {
      toast.error(Vocabulary.saveNoteError);
      return;
    }

    setIsSaving(true);
    try {
      await postData(`${endpoints.messages.main}/${messageId}/notes`, { text: noteText });

      // Reload notes to get the updated list with populated agent data
      await loadNotes();
      setNoteText('');
      toast.success(Vocabulary.noteSaved);
    } catch (error) {
      console.error(`${Vocabulary.saveNoteError}:`, error);
      toast.error(Vocabulary.saveNoteError);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!messageId) {
      toast.error(Vocabulary.deleteNoteError);
      return;
    }

    try {
      await deleteData(`${endpoints.messages.main}/${messageId}/notes/${noteId}`);

      // Reload notes to get the updated list
      await loadNotes();
      toast.success(Vocabulary.noteDeleted);
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error(Vocabulary.deleteNoteError);
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('ro-RO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: 'fixed',
          top: '50%',
          transform: 'translateY(-50%)',
          m: 0,
          maxHeight: '85vh',
          maxWidth: { xs: 'calc(100vw - 32px)', sm: 600 },
          width: '100%',
          borderRadius: 2,
          boxShadow: 6,
          overflowX: 'hidden'
        }
      }}
      sx={{ zIndex: 1350 }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: theme.palette.primary.light,
          color: '#fff',
          py: 1.5,
          px: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, flexShrink: 0 }}>
            {Vocabulary.notes}
          </Typography>
          {messageName && (
            <Chip
              label={messageName}
              size="small"
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: '#fff',
                fontWeight: 500,
                height: 24,
                maxWidth: { xs: 150, sm: 250 },
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block'
                }
              }}
            />
          )}
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: '#fff',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 0, overflowX: 'hidden' }}>
        {/* New Note Input */}
        <Box sx={{ p: 2, backgroundColor: theme.palette.mode === 'light' ? '#fafafa' : 'rgba(255,255,255,0.05)' }}>
          <Typography variant="caption" sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary', display: 'block' }}>
            {Vocabulary.addNewNote}
          </Typography>
          <TextField
            multiline
            rows={3}
            fullWidth
            placeholder={Vocabulary.writeNoteHere}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                backgroundColor: theme.palette.background.paper,
                borderRadius: 1.5,
                width: '100%'
              },
              '& .MuiInputBase-input': {
                overflowX: 'hidden',
                wordWrap: 'break-word',
                wordBreak: 'break-word'
              }
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<Save />}
              onClick={handleSaveNote}
              disabled={isSaving || !noteText.trim()}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5
              }}
            >
              {Vocabulary.saveNote}
            </Button>
          </Box>
        </Box>

        <Divider />

        {/* Notes List */}
        <Box sx={{ p: 2, maxHeight: '50vh', overflowY: 'auto', overflowX: 'hidden' }}>
          <Typography variant="caption" sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary', display: 'block' }}>
            {Vocabulary.savedNotes} ({notes.length})
          </Typography>

          {notes.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                textAlign: 'center',
                backgroundColor: theme.palette.mode === 'light' ? '#f5f5f5' : 'rgba(0,0,0,0.2)',
                borderRadius: 1.5
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {Vocabulary.noSavedNotes}
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={1.5}>
              {notes.map((note) => (
                <Paper
                  key={note._id}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: 1,
                      borderColor: theme.palette.primary.light
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarToday sx={{ fontSize: 12, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary" fontSize={11}>
                        {formatDate(note.createdAt)}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteNote(note._id)}
                      sx={{
                        p: 0.5,
                        color: 'error.main',
                        '&:hover': {
                          backgroundColor: 'error.light',
                          color: 'error.dark'
                        }
                      }}
                    >
                      <Delete sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>

                  <Typography
                    variant="body2"
                    fontSize={13}
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: 1.5,
                      color: 'text.primary',
                      mb: note.agent ? 1 : 0
                    }}
                  >
                    {note.text}
                  </Typography>

                  {note.agent && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, pt: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
                      <Person sx={{ fontSize: 12, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary" fontStyle="italic" fontSize={11}>
                        {typeof note.agent === 'string' ? note.agent : `${note.agent.firstName} ${note.agent.lastName}`}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
